import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Tooltip } from '@mui/material';

// Contador da janela de atendimento.
//
// Recebe o prazo final já calculado (getWindowExpiresAt, em
// src/utils/serviceWindow.utils.js) em vez da data da última mensagem: a janela não é
// sempre "última mensagem + 24h" — conversa vinda de anúncio (Click-to-WhatsApp) tem 72h,
// e grupo conta de outro campo. `isCtwa` diz se é o prazo do anúncio que está mandando,
// para a tela explicar por que essa conversa tem mais tempo que as outras.
const WhatsAppCountdownTimer = ({ expiresAt, isCtwa = false }) => {
  const { t } = useTranslation('chat');
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  const deadline = expiresAt ? new Date(expiresAt).getTime() : null;

  useEffect(() => {
    if (!deadline) return;

    const calculateTimeLeft = () => {
      const difference = deadline - Date.now();

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return;
      }

      setIsExpired(false);

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0')
      });
    };

    // Calcula imediatamente
    calculateTimeLeft();

    // Atualiza a cada segundo
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [deadline]);

  if (!deadline || isExpired) {
    return null;
  }

  if (!timeLeft) {
    return null;
  }

  // Limites absolutos: continuam valendo numa janela de 72h (o que importa é quanto
  // falta, não qual era o prazo total).
  const getColorClass = () => {
    const totalMinutes = parseInt(timeLeft.hours) * 60 + parseInt(timeLeft.minutes);

    if (totalMinutes <= 60) {
      return 'text-red-600'; // Vermelho quando restam menos de 1 hora
    } else if (totalMinutes <= 180) {
      return 'text-orange-600'; // Laranja quando restam menos de 3 horas
    } else {
      return 'text-green-600'; // Verde para mais de 3 horas
    }
  };

  const getIconColor = () => {
    const totalMinutes = parseInt(timeLeft.hours) * 60 + parseInt(timeLeft.minutes);

    if (totalMinutes <= 60) {
      return 'error'; // Vermelho quando restam menos de 1 hora
    } else if (totalMinutes <= 180) {
      return 'warning'; // Laranja quando restam menos de 3 horas
    } else {
      return 'success'; // Verde para mais de 3 horas
    }
  };

  return (
    <Tooltip title={isCtwa ? t('window.tooltipCtwa') : t('window.tooltip')}>
      <div
        className={`ml-2 flex items-center gap-1 px-2 py-1 border rounded-md ${isCtwa ? 'bg-violet-50 border-violet-200' : 'bg-blue-50 border-blue-200'}`}
      >
        <Iconify
          icon="mingcute:time-line"
          color={getIconColor()}
          width={14}
          height={14}
        />
        <span className={`text-xs font-medium ${getColorClass()}`}>
          {timeLeft.hours}:{timeLeft.minutes}
        </span>
        {/* Deixa explícito por que essa conversa tem mais prazo que as outras. */}
        {isCtwa && (
          <span className="flex items-center gap-0.5 pl-1 ml-0.5 border-l border-violet-200 text-[10px] font-semibold text-violet-700">
            <Iconify icon="eva:speaker-outline" width={12} height={12} />
            {t('window.ctwaBadge')}
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export default WhatsAppCountdownTimer;
